/**
 * SkillCommissioningPage — operator route for commissioning / decommissioning
 * orchestrator-class skills (WQ-245 Phase 4.10 β10).
 *
 * Per Owen Decision 1 (2026-05-03): the GUI commissioning surface is the
 * sole permitted human path to flip PlatformSkillLibrary.enabled. Backend
 * defends in depth via SkillLibraryService.set_enabled() actor_type=HUMAN
 * guard.
 *
 * WQ-263 Scope B.2 modal decoupling retrofit (2026-05-05):
 * The "8-Obligation Summary" modal previously computed green checks from
 * JSON-shape presence checks (5 booleans) plus 3 hardcoded ✅ marks for
 * Obligations 4/6/8. The 2026-05-05 production deviation surfaced this
 * gap: an operator clicked Commission in good faith based on misleading
 * greens while the localhost ceremony's Revise outcome (4 Fails) was not
 * surfaced anywhere in the UI.
 *
 * The retrofit:
 *   - Fetches GET /api/admin/skills/{slug}/obligations on modal open
 *   - Renders per-obligation Pass/Fail/Hold/N/A from real evaluator output
 *     via the new ObligationItem component
 *   - Renders an amber banner parameterized off the evaluator response
 *     when aggregate is not Authorize (banner inherits to future
 *     ceremonies on other orchestrators without modal code changes)
 *   - Disables Confirm when any obligation Fails (regardless of rationale
 *     text); requires a separate override_acknowledgment textarea when
 *     any obligation is Hold-for-question; persistent inline
 *     disabled-reason instead of hover tooltip per Owen 2026-05-05.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listSkills,
  commissionSkill,
  getSkillObligations,
  type SkillSummary,
  type ObligationEvaluatorResponse,
} from '../services/skillCommissioningService';
import ObligationItem from '../components/skill-commissioning/ObligationItem';

// WQ-269 — body text + icon parameterized by current evaluator aggregate.
// Closes the fourth manifestation of the Evaluator Honesty Pattern
// (`_TRACKING/EVALUATOR_HONESTY_PATTERN.md` Addendum 2): WQ-268 fixed the
// banner header to show the most-recent ceremony outcome (AUTHORIZE 2026-05-06)
// but the body + warning emoji were hardcoded for the Revise case, producing
// internal contradictions when the live aggregate is `hold`. The render
// conditional already gates on `aggregate_result !== 'authorize'` so this
// helper only ever sees revise/hold/pass_with_notes; the default branch is
// defensive.
//
// WQ-274 — extended with `pass_with_notes` branch for advisory-archetype
// Path Y disposition (OpsAgent first commissioning). PWN aggregate carries
// no Holds, so override_acknowledgment is NOT required; reviewer-judgment
// substance lives in the IL4_REVIEW ceremony record at Phase 3 sealing.
function bannerBodyText(aggregate: string): string {
  switch (aggregate) {
    case 'revise':
      return 'This skill cannot be commissioned until outstanding obligation gaps are closed. Current evaluator status:';
    case 'hold':
      return 'This skill has obligations marked Hold-for-question requiring reviewer judgment. Override acknowledgment required to commission. Current evaluator status:';
    case 'pass_with_notes':
      return 'This skill has obligations marked Pass-with-notes; reviewer-judgment substance is recorded in the IL-4 ceremony record rather than at this modal. Confirm enables on rationale alone. Current evaluator status:';
    default:
      return 'Current evaluator status:';
  }
}

function bannerIcon(aggregate: string): string {
  // ⚠️ warns of a blocker; ℹ️ signals reviewer-judgment required (not blocked).
  // pass_with_notes is not a blocker (zero Holds → no override required).
  return aggregate === 'revise' ? '⚠️' : 'ℹ️';
}

// WQ-274 — banner header is honest about whether a prior IL-4 review record
// exists on disk. When `outcome === null` (no IL4_REVIEW_<UPPER_SLUG>_v<N>_<DATE>.md
// on disk for the skill), returns "First commissioning — no prior IL-4 review on
// record" rather than substituting the live evaluator aggregate as if it were a
// prior outcome.
//
// 5th manifestation of the Evaluator Honesty Pattern
// (`_TRACKING/EVALUATOR_HONESTY_PATTERN.md` Addendum 3) — surfaced by the DEF-011d
// pre-resumption modal dry run 2026-05-10 when the ops-agent modal showed
// "Most recent IL-4 review result: HOLD" despite no prior record on disk. WQ-263
// / WQ-268 / WQ-269 fixed the banner for the case where a prior record exists
// but is misidentified; this is the same class of dishonesty surfaced one layer
// further out (no prior record at all).
//
// Strict null check is intentional: an empty-string outcome from the backend
// would indicate a parse failure at `_parse_review_aggregate_outcome`, which
// per WQ-268 design returns None on parse failure. Callers should never see an
// empty string here — if they do, it's a backend bug to investigate, not a
// banner-rendering case to handle.
export function bannerHeaderText(
  outcome: string | null | undefined,
  date: string | null | undefined,
): string {
  if (outcome == null) {
    return 'First commissioning — no prior IL-4 review on record';
  }
  return `Most recent IL-4 review (${date ?? 'unknown date'}) result: ${outcome.toUpperCase()}`;
}

const SkillCommissioningPage: React.FC = () => {
  const { authFetch } = useAuth();
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalSkill, setModalSkill] = useState<SkillSummary | null>(null);
  const [modalAction, setModalAction] = useState<'commission' | 'decommission'>('commission');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // WQ-263 Scope B.2 state — per-obligation evaluation fetched on modal open;
  // override_acknowledgment captured when any obligation is Hold-for-question.
  const [obligations, setObligations] = useState<ObligationEvaluatorResponse | null>(null);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [obligationsError, setObligationsError] = useState<string | null>(null);
  const [overrideAcknowledgment, setOverrideAcknowledgment] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSkills(authFetch);
      setSkills(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // WQ-263: fetch obligations when modal opens for commission action
  // (decommission action does NOT gate on obligations — operator may
  // need to decommission a previously-Authorized skill that has since
  // drifted, which is what the rollback plan covers).
  useEffect(() => {
    if (!modalSkill || modalAction !== 'commission') {
      setObligations(null);
      setObligationsError(null);
      return;
    }
    let cancelled = false;
    setObligationsLoading(true);
    setObligationsError(null);
    getSkillObligations(authFetch, modalSkill.slug)
      .then((data) => {
        if (!cancelled) setObligations(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setObligationsError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setObligationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalSkill, modalAction, authFetch]);

  const openModal = (skill: SkillSummary, action: 'commission' | 'decommission') => {
    setModalSkill(skill);
    setModalAction(action);
    setRationale('');
    setOverrideAcknowledgment('');
    setError(null);
  };

  const closeModal = () => {
    setModalSkill(null);
    setRationale('');
    setOverrideAcknowledgment('');
  };

  // WQ-263: Confirm-button gating logic.
  // - any Fail        → disabled (regardless of rationale)
  // - any Hold + no override → disabled
  // - rationale < 10  → disabled (existing)
  const hasFail = obligations?.aggregate_result === 'revise';
  const hasHold = obligations?.aggregate_result === 'hold';
  const overrideRequired = hasHold && modalAction === 'commission';
  const overrideMissing = overrideRequired && overrideAcknowledgment.trim().length < 10;
  const rationaleMissing = rationale.trim().length < 10;
  const commissionBlocked = modalAction === 'commission' && (hasFail || overrideMissing);
  const confirmDisabled = submitting || rationaleMissing || commissionBlocked;

  // Persistent inline disabled-reason — surfaces WHY the button is
  // disabled without requiring hover (per Owen 2026-05-05 refinement).
  let disabledReason: string | null = null;
  if (modalAction === 'commission' && hasFail) {
    const failCount = obligations?.counts.fail ?? 0;
    disabledReason = `Cannot commission: ${failCount} obligation(s) Fail. Resolve gaps via WQ-263 closure + ceremony re-run.`;
  } else if (modalAction === 'commission' && overrideRequired && overrideMissing) {
    const holdCount = obligations?.counts.hold ?? 0;
    disabledReason = `Cannot commission: ${holdCount} obligation(s) Hold-for-question — override acknowledgment required above.`;
  } else if (rationaleMissing) {
    disabledReason = 'Cannot proceed: rationale required (≥10 chars).';
  }

  const handleConfirm = async () => {
    if (!modalSkill) return;
    if (rationale.trim().length < 10) {
      setError('Rationale must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      // WQ-263: combine rationale + override acknowledgment into the
      // submitted rationale so both are recorded in gate_decisions.
      // Backend stores in metadata; current commission endpoint accepts
      // a single rationale string. Concatenation preserves both texts in
      // the audit record without requiring backend schema change at
      // WQ-263 ship — future enhancement may split into separate fields
      // on the gate_decisions row directly.
      const combinedRationale = overrideAcknowledgment.trim()
        ? `${rationale.trim()}\n\n[Override acknowledgment for Hold-for-question items]\n${overrideAcknowledgment.trim()}`
        : rationale.trim();
      await commissionSkill(
        authFetch,
        modalSkill.slug,
        modalAction === 'commission',
        combinedRationale,
      );
      closeModal();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const orchestratorSkills = skills.filter((s) => s.manifest_summary !== null);
  const otherSkills = skills.filter((s) => s.manifest_summary === null);

  return (
    <div className="page-container" style={{ padding: 'var(--space-6)' }}>
      <header style={{ marginBottom: 'var(--space-5)' }}>
        <h1 style={{ marginBottom: 'var(--space-1)' }}>Skill Commissioning</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
          Commission orchestrator-class skills for invocation. Per AIPOA pattern §5
          (review-only commissioning), invocation requires explicit human commissioning.
          See Contract v1.2.0 §11.
        </p>
      </header>

      {error && !modalSkill && (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            background: 'rgba(220,38,38,0.1)',
            border: '1px solid rgba(220,38,38,0.4)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger, #dc2626)',
            fontSize: 'var(--text-sm)',
            marginBottom: 'var(--space-4)',
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>
      )}

      <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-3)' }}>
        Orchestrator-class skills ({orchestratorSkills.length})
      </h2>
      <table className="data-table" style={{ width: '100%', fontSize: '13px' }}>
        <thead>
          <tr>
            <th>Slug</th>
            <th>Name</th>
            <th>Contract</th>
            <th>Tier</th>
            <th>IL</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {orchestratorSkills.map((s) => (
            <tr key={s.id}>
              <td><code>{s.slug}</code></td>
              <td>{s.name}</td>
              <td>{s.contract_version || '—'}</td>
              <td>{s.tier || '—'}</td>
              <td>{s.integrity_level || '—'}</td>
              <td>
                <span className={s.enabled ? 'badge badge-success' : 'badge badge-warning'}>
                  {s.enabled ? 'enabled' : 'disabled'}
                </span>
              </td>
              <td>
                {s.enabled ? (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => openModal(s, 'decommission')}
                  >
                    Decommission
                  </button>
                ) : (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openModal(s, 'commission')}
                  >
                    Commission
                  </button>
                )}
              </td>
            </tr>
          ))}
          {orchestratorSkills.length === 0 && !loading && (
            <tr>
              <td colSpan={7} style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 'var(--space-4)' }}>
                No orchestrator-class skills registered.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {otherSkills.length > 0 && (
        <details style={{ marginTop: 'var(--space-6)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>
            Other registered skills ({otherSkills.length})
          </summary>
          <table className="data-table" style={{ width: '100%', fontSize: '13px', marginTop: 'var(--space-3)' }}>
            <thead>
              <tr>
                <th>Slug</th>
                <th>Name</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              {otherSkills.map((s) => (
                <tr key={s.id}>
                  <td><code>{s.slug}</code></td>
                  <td>{s.name}</td>
                  <td>{s.version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {modalSkill && (
        <div
          className="modal-overlay"
          onClick={closeModal}
          data-testid="skill-commissioning-modal"
        >
          <div
            className="modal modal-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="commission-modal-title"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2 id="commission-modal-title" className="modal-title">
                {modalAction === 'commission' ? 'Commission' : 'Decommission'} <code>{modalSkill.slug}</code>
              </h2>
              <button
                className="btn btn-ghost"
                onClick={closeModal}
                aria-label="Close"
                disabled={submitting}
              >
                {'\u00d7'}
              </button>
            </div>

            <div className="modal-body" style={{ display: 'grid', gap: 'var(--space-4)' }}>
              {error && (
                <div
                  role="alert"
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'rgba(220,38,38,0.1)',
                    border: '1px solid rgba(220,38,38,0.4)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--danger, #dc2626)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  {error}
                </div>
              )}

              {/* WQ-263 Scope B.2: Revise-outcome banner — parameterized off
                  the evaluator response so it inherits to future ceremonies
                  on other orchestrators without modal code changes. */}
              {modalAction === 'commission' && obligations && obligations.aggregate_result !== 'authorize' && (
                <div
                  data-testid="obligations-banner"
                  data-aggregate={obligations.aggregate_result}
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    background: 'rgba(245,158,11,0.15)',
                    border: '1px solid #f59e0b',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  <strong style={{ color: '#92400e' }}>
                    {bannerIcon(obligations.aggregate_result)}{' '}
                    {bannerHeaderText(
                      obligations.most_recent_review_outcome,
                      obligations.most_recent_review_date,
                    )}
                  </strong>
                  <p style={{ margin: 'var(--space-2) 0 0 0' }}>
                    {bannerBodyText(obligations.aggregate_result)}
                  </p>
                  <ul style={{ margin: 'var(--space-1) 0 0 var(--space-4)' }}>
                    {obligations.counts.fail > 0 && (
                      <li>{obligations.counts.fail} obligation(s) <strong>FAIL</strong> — see per-row detail below</li>
                    )}
                    {obligations.counts.hold > 0 && (
                      <li>{obligations.counts.hold} obligation(s) <strong>HOLD-FOR-QUESTION</strong> — override acknowledgment required</li>
                    )}
                    {obligations.counts.pass_with_notes > 0 && (
                      <li>{obligations.counts.pass_with_notes} obligation(s) <strong>PASS-WITH-NOTES</strong> — read notes; commission OK</li>
                    )}
                    {obligations.counts.pass > 0 && (
                      <li>{obligations.counts.pass} obligation(s) <strong>PASS</strong> — clean</li>
                    )}
                    {obligations.counts.n_a > 0 && (
                      <li>{obligations.counts.n_a} obligation(s) <strong>N/A</strong> — justified</li>
                    )}
                  </ul>
                  {obligations.most_recent_review_record_path && (
                    <p style={{ margin: 'var(--space-2) 0 0 0', fontSize: 'var(--text-xs)' }}>
                      Audit record: <code>{obligations.most_recent_review_record_path}</code>
                    </p>
                  )}
                </div>
              )}

              {/* WQ-263 Scope B.2: per-obligation list from real evaluator. */}
              {modalAction === 'commission' && (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-secondary)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                    8-Obligation Summary
                  </strong>
                  {obligationsLoading && (
                    <p data-testid="obligations-loading" style={{ color: 'var(--text-muted)' }}>
                      Loading obligation evaluator…
                    </p>
                  )}
                  {obligationsError && (
                    <p data-testid="obligations-error" role="alert" style={{ color: 'var(--danger, #dc2626)' }}>
                      Obligation evaluator unreachable: {obligationsError}
                    </p>
                  )}
                  {obligations && (
                    <ul
                      data-testid="obligations-list"
                      style={{ margin: 0, padding: 0, fontSize: 'var(--text-sm)' }}
                    >
                      {obligations.obligations.map((o) => (
                        <ObligationItem
                          key={o.obligation_number}
                          obligation={o}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)' }}>
                  Commission rationale (required){' '}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 'var(--weight-normal)' }}>
                    (min 10 characters; recorded in gate decision)
                  </span>
                </span>
                <textarea
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  rows={4}
                  className="form-input"
                  placeholder={
                    modalAction === 'commission'
                      ? 'IL4_AGENT_REVIEW.md complete; all 8 obligations satisfied; first commissioning approved YYYY-MM-DD'
                      : 'Drift detected: <metric>=<value>; investigating per <link>'
                  }
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 'var(--text-sm)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--bg-input, var(--bg-elevated))',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    resize: 'vertical',
                  }}
                />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {rationale.trim().length}/10 chars minimum
                </span>
              </label>

              {/* WQ-263 Scope B.2: separate override acknowledgment field
                  rendered ONLY when any obligation is Hold-for-question.
                  Stacked below standard rationale; separate JSON keys
                  (rationale + override_acknowledgment) per Owen 2026-05-05. */}
              {overrideRequired && (
                <label
                  data-testid="override-acknowledgment-field"
                  style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}
                >
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)' }}>
                    Override acknowledgment (required —{' '}
                    {obligations?.counts.hold ?? 0} item(s) Hold-for-question)
                  </span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    You are overriding {obligations?.counts.hold ?? 0} item(s) marked
                    Hold-for-question by the obligation evaluator. Document your
                    reviewer judgment for each Hold item below. This text is recorded
                    in the gate_decisions row alongside the standard rationale.
                  </span>
                  <textarea
                    value={overrideAcknowledgment}
                    onChange={(e) => setOverrideAcknowledgment(e.target.value)}
                    rows={3}
                    className="form-input"
                    placeholder="Reviewer judgment: Obligation X Subitem Y is Pass-with-notes per [evidence]; Subitem Z is N/A per [reason]."
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 'var(--text-sm)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--bg-input, var(--bg-elevated))',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                      resize: 'vertical',
                    }}
                  />
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    {overrideAcknowledgment.trim().length}/10 chars minimum
                  </span>
                </label>
              )}
            </div>

            <div className="modal-footer">
              {/* WQ-263 Scope B.2: persistent inline disabled-reason
                  (no hover tooltip per Owen 2026-05-05). */}
              {disabledReason && (
                <span
                  data-testid="confirm-disabled-reason"
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-muted)',
                    marginRight: 'auto',
                  }}
                >
                  {disabledReason}
                </span>
              )}
              <button
                className="btn btn-ghost"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={modalAction === 'commission' ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={handleConfirm}
                disabled={confirmDisabled}
                data-testid="confirm-button"
              >
                {submitting ? 'Submitting…' : `Confirm ${modalAction}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillCommissioningPage;
